import React, { useEffect, useRef, useState } from 'react';
import CloudMetricCharts from './CloudMetricCharts';
import { cloudRead, saveCloudCsv } from '../../services/cloudReadApi';
import { useAuth } from '../../contexts/AuthContext';
import filterLiveMachines from './filterLiveMachines';

const labels = {
  machines: 'Máquinas virtuales', networks: 'Redes / VPC / VCN', subnets: 'Subredes',
  load_balancers: 'Balanceadores', security_groups: 'Grupos de seguridad / Firewall',
  disks: 'Discos de datos', snapshots: 'Snapshots', buckets: 'Buckets',
};

export default function CloudReadPanel({ mode = 'policies' }) {
  const { hasPermission } = useAuth();
  const permission = mode === 'policies' ? 'ops.monitoreo.view'
    : mode === 'metrics' ? 'ops.monitoreo.view' : 'ops.inventario.view';
  const allowed = hasPermission(permission);
  const [options, setOptions] = useState([]);
  const [support, setSupport] = useState({});
  const [reference, setReference] = useState('');
  const [kind, setKind] = useState('machines');
  const [minutes, setMinutes] = useState('15');
  const [instance, setInstance] = useState('');
  const [machines, setMachines] = useState([]);
  const [machineSearch, setMachineSearch] = useState('');
  const filteredMachines = filterLiveMachines(machines, machineSearch);
  const [instancesLoaded, setInstancesLoaded] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const request = useRef(null);
  const revision = useRef(0);
  useEffect(() => {
    if (!allowed) return undefined;
    const controller = new AbortController();
    setBusy(true);
    cloudRead('options', {}, controller.signal).then(data => {
      if (!Array.isArray(data.accesses)) throw new Error('Catálogo de accesos no válido.');
      const items = mode === 'policies' ? data.accesses : data.accesses.filter(item => item.provider === 'oci');
      setOptions(items);
      setSupport(data.supported_resources || {});
      setReference(items[0]?.reference || '');
    }).catch(err => {
      if (err.name !== 'AbortError') setError(err.message);
    }).finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => { controller.abort(); request.current?.abort(); };
  }, [allowed, mode]);

  const reset = value => {
    revision.current += 1;
    request.current?.abort();
    setReference(value);
    setResult(null);
    setMachines([]);
    setMachineSearch('');
    setInstancesLoaded(false);
    setInstance('');
    setError('');
    setBusy(false);
  };
  const run = async (operation, params) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const current = ++revision.current;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const data = await cloudRead(operation, { reference, ...params }, controller.signal);
      if (current !== revision.current) return;
      if (operation === 'inventory' && mode === 'metrics') {
        if (!Array.isArray(data.resources)) throw new Error('Lista de instancias no válida.');
        setMachines(data.resources);
        setMachineSearch('');
        setInstancesLoaded(true);
        setInstance(data.resources[0]?.id || '');
      } else {
        if (operation === 'policies' && (!Array.isArray(data.policies) || typeof data.csv !== 'string')) {
          throw new Error('Inventario de políticas no válido.');
        }
        if (operation === 'inventory' && !Array.isArray(data.resources)) throw new Error('Inventario no válido.');
        if (operation === 'metrics' && (!data.metrics || !data.metric_status)) throw new Error('Métricas no válidas.');
        setResult(data);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && current === revision.current) setError(err.message);
    } finally {
      if (current === revision.current) setBusy(false);
    }
  };
  if (!allowed) return <p role="alert">No tienes permisos para consultar este módulo.</p>;
  const selected = options.find(item => item.reference === reference);
  const rows = result?.policies || result?.resources || [];
  const title = mode === 'policies' ? 'Políticas de monitoreo'
    : mode === 'metrics' ? 'Métricas OCI en vivo' : 'Inventario OCI en vivo';
  return (
    <section className="rounded-2xl border border-violet-400/20 bg-[#181024]/90 p-5 space-y-5">
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="text-sm text-white/65">
        Consulta bajo demanda de solo lectura. No modifica recursos, no usa IA y no guarda capturas históricas.
        {mode === 'policies' && ' No es el Centro de Alertas: muestra reglas configuradas; no verifica métricas ni entrega de notificaciones.'}
      </p>
      {error && <p role="alert" className="text-red-300">{error}</p>}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">Cuenta / acceso de Key Vault
          <select aria-label="Acceso cloud" value={reference} disabled={busy}
            onChange={event => reset(event.target.value)}
            className="block rounded-lg bg-[#25183b] p-2 border border-white/20 max-w-full">
            {!options.length && <option value="">Sin accesos configurados</option>}
            {options.map(item => <option key={item.reference} value={item.reference}>
              {item.provider.toUpperCase()} · {item.customer} · {item.reference}
            </option>)}
          </select>
        </label>
        {mode === 'inventory' && <label>Tipo de recurso
          <select aria-label="Tipo de recurso OCI" value={kind} disabled={busy} onChange={event => {
            setKind(event.target.value); setResult(null);
          }} className="block bg-[#25183b] rounded-lg p-2">
            {(support[selected?.provider] || ['machines']).map(value => <option key={value} value={value}>{labels[value] || value}</option>)}
          </select>
        </label>}
        <button disabled={busy || !reference} className="rounded-lg bg-violet-600 px-4 py-2 disabled:opacity-40"
          onClick={() => run(mode === 'policies' ? 'policies' : 'inventory', mode === 'policies' ? {} : { kind: mode === 'metrics' ? 'machines' : kind })}>
          {busy ? 'Consultando…' : mode === 'metrics' ? 'Buscar instancias OCI' : 'Consultar en vivo'}
        </button>
        {result?.csv && <button className="rounded-lg border border-white/20 px-4 py-2"
          onClick={() => saveCloudCsv(result.csv, `politicas-${result.provider}-${result.queried_at.slice(0, 10)}.csv`)}>
          Exportar políticas CSV
        </button>}
      </div>
      {!busy && !error && !options.length && <p>{mode === 'policies'
        ? 'Configura accesos cloud habilitados en Key Vault para consultar sus políticas.'
        : 'Configura un secreto OCI con sus regiones y compartimentos explícitos en Key Vault para habilitarlo.'}</p>}
      {mode === 'metrics' && instancesLoaded && !machines.length && !busy && !error
        && <p>Consulta completada: no hay instancias OCI en el alcance configurado.</p>}
      {mode === 'metrics' && machines.length > 0 && <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm">Buscar instancia OCI por nombre o ID
          <input type="search" value={machineSearch} disabled={busy}
            placeholder="Pega el nombre o ID de la VM"
            aria-describedby="oci-vm-search-status"
            className="block bg-[#25183b] p-2 rounded-lg border border-white/20"
            onChange={event => {
              const value = event.target.value;
              setMachineSearch(value);
              if (!filterLiveMachines(machines, value).some(machine => machine.id === instance)) {
                setInstance('');
                setResult(null);
              }
            }} />
          <span id="oci-vm-search-status" role="status" className="block text-xs text-white/65">
            {filteredMachines.length} de {machines.length} instancias coinciden
          </span>
        </label>
        <label>Instancia OCI
          <select aria-label="Instancia OCI" className="block bg-[#25183b] p-2 rounded-lg" value={instance}
            disabled={busy} onChange={event => { setInstance(event.target.value); setResult(null); }}>
            {!instance && <option value="">{filteredMachines.length ? 'Selecciona una instancia' : 'Sin coincidencias'}</option>}
            {filteredMachines.map(machine => <option key={machine.id} value={machine.id}>{machine.name} · {machine.region}</option>)}
          </select>
        </label>
        <label>Rango
          <select aria-label="Rango OCI" className="block bg-[#25183b] p-2 rounded-lg" value={minutes}
            disabled={busy} onChange={event => { setMinutes(event.target.value); setResult(null); }}>
            {[15, 60, 360, 1440, 10080].map(value => <option key={value} value={value}>{value} minutos</option>)}
          </select>
        </label>
        <button disabled={busy || !instance} onClick={() => run('metrics', { instance_id: instance, minutes })}
          className="rounded-lg bg-violet-600 px-4 py-2 disabled:opacity-40">Consultar métricas OCI</button>
      </div>}
      {result && <p className="text-xs text-white/60">
        Consultado: {result.queried_at} · {result.total !== undefined && `${result.total} registros`}
        {result.partial && ' · Consulta parcial: revisa los errores por métrica'}
      </p>}
      {result?.scope && <p className="text-xs text-white/60 break-words">
        Alcance consultado: {JSON.stringify(result.scope)}
      </p>}
      {result && mode !== 'metrics' && rows.length === 0 && <p>Consulta completada: no se encontraron registros en el alcance seleccionado.</p>}
      {result && mode === 'policies' && rows.length > 0 && <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr>{['Nombre', 'Nube', 'Cuenta / proyecto', 'Región', 'Tipo', 'Habilitada', 'Estado', 'Datos', 'Destinos'].map(label =>
            <th key={label} className="p-2 border-b border-white/20">{label}</th>)}</tr></thead>
          <tbody>{rows.map((item, index) => <tr key={`${item.monitor_id}:${index}`}>
            {[item.monitor_name, item.cloud, item.project, item.region, item.monitor_type,
              item.enabled === true ? 'Sí' : item.enabled === false ? 'No' : 'UNVERIFIED',
              item.state, item.data_status, item.notification_count].map((value, column) =>
              <td key={column} className="p-2 border-b border-white/10">{String(value ?? 'UNVERIFIED')}</td>)}
          </tr>)}</tbody>
        </table>
        <p className="text-xs text-white/60 mt-2">Detalles completos por política debajo. Habilitada no significa que haya datos o entrega verificada.</p>
      </div>}
      {rows.map((item, index) => <details key={`${item.monitor_id || item.id}:${index}`} className="rounded-xl border border-white/10 p-3">
        <summary className="cursor-pointer">{item.monitor_name || item.name} · {item.region} · {item.monitor_type || kind}</summary>
        <dl className="space-y-2 mt-3 text-xs">
          {Object.entries(item).map(([field, value]) => <div key={field}>
            <dt className="text-violet-200">{field}</dt>
            <dd className="whitespace-pre-wrap break-all">{value === null ? 'No informado' : typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</dd>
          </div>)}
        </dl>
      </details>)}
      {result?.metric_status && <CloudMetricCharts result={result} />}
    </section>
  );
}
